/**
 * ZenRIS 5.0 - Study Status Constants
 * 
 * Aceste constante definesc statusurile standardizate pentru studii.
 * Backward compatibility: sistemul verifică atât workflow_status (nou) cât și status (legacy).
 */

// Workflow Status - noile statusuri standardizate
export enum StudyWorkflowStatus {
  INCOMING = 'incoming',       // Studiu în procesare (sync în curs)
  NEW = 'new',                 // Studiu gata, nealocat
  ASSIGNED = 'assigned',       // Alocat unui medic, nu a început
  IN_PROGRESS = 'in_progress', // Raport în lucru (draft)
  COMPLETED = 'completed',     // Raport finalizat și semnat
  EXTERNAL = 'external',       // Upload manual (CD/USB)
  HISTORICAL = 'historical',   // Tras pentru comparație/istoric
}

// Priority - prioritate studiu
export enum StudyPriority {
  ROUTINE = 'routine',   // Normal
  URGENT = 'urgent',     // Urgent
  STAT = 'stat',         // Super-urgent (Short Turn Around Time)
}

// Assignment Source - sursa alocării
export enum AssignmentSource {
  AUTO = 'auto',             // Auto-assign din gardă activă
  MANUAL = 'manual',         // Alocare manuală de operator/admin
  SELF = 'self',             // Medicul și-a luat singur studiul
  COORDINATOR = 'coordinator', // Coordonator de instituție alocă
}

// Legacy Status - pentru backward compatibility
// Acestea vor fi deprecate în versiuni viitoare
export const LegacyStatus = {
  NOU: 'nou',
  ALOCAT: 'alocat',
  ALOCAT_DRAFT: 'alocat (draft)',
  ALOCAT_FINALIZAT: 'alocat (finalizat)',
  IN_CURS: 'în_curs',
  FINALIZAT: 'finalizat',
} as const;

// Mapping legacy → workflow status
export const legacyToWorkflowStatus: Record<string, StudyWorkflowStatus> = {
  'nou': StudyWorkflowStatus.NEW,
  'neasociat': StudyWorkflowStatus.NEW,
  'alocat': StudyWorkflowStatus.ASSIGNED,
  'alocat (draft)': StudyWorkflowStatus.IN_PROGRESS,
  'alocat (finalizat)': StudyWorkflowStatus.COMPLETED,
  'în_curs': StudyWorkflowStatus.IN_PROGRESS,
  'finalizat': StudyWorkflowStatus.COMPLETED,
  'extern': StudyWorkflowStatus.EXTERNAL,
  'istoric': StudyWorkflowStatus.HISTORICAL,
  'pending': StudyWorkflowStatus.INCOMING,
  'incoming': StudyWorkflowStatus.INCOMING,
};

/**
 * Get the effective workflow status from a study object
 * Handles both new workflow_status and legacy status fields
 */
export function getEffectiveStatus(study: { workflow_status?: string; status?: string }): StudyWorkflowStatus {
  // Prefer workflow_status if available
  if (study.workflow_status) {
    return study.workflow_status as StudyWorkflowStatus;
  }
  
  // Fall back to legacy status
  if (study.status) {
    const mapped = legacyToWorkflowStatus[study.status.toLowerCase()];
    if (mapped) return mapped;
  }
  
  return StudyWorkflowStatus.NEW;
}

/**
 * Check if a study is in a specific workflow status
 * Handles both new and legacy status fields
 */
export function isStatus(
  study: { workflow_status?: string; status?: string },
  ...statuses: StudyWorkflowStatus[]
): boolean {
  const effective = getEffectiveStatus(study);
  return statuses.includes(effective);
}

/**
 * Check if a study is finalized (completed)
 */
export function isFinalized(study: { workflow_status?: string; status?: string }): boolean {
  return isStatus(study, StudyWorkflowStatus.COMPLETED);
}

/**
 * Check if a study is in progress (draft)
 */
export function isInProgress(study: { workflow_status?: string; status?: string }): boolean {
  return isStatus(study, StudyWorkflowStatus.IN_PROGRESS);
}

/**
 * Check if a study is new/unassigned
 */
export function isNew(study: { workflow_status?: string; status?: string }): boolean {
  return isStatus(study, StudyWorkflowStatus.NEW, StudyWorkflowStatus.INCOMING);
}

/**
 * Check if a study is assigned but not started
 */
export function isAssigned(study: { workflow_status?: string; status?: string }): boolean {
  return isStatus(study, StudyWorkflowStatus.ASSIGNED);
}

// UI Display helpers

export interface StatusDisplay {
  label: string;
  color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  bgColor: string;
}

export const statusDisplayMap: Record<StudyWorkflowStatus, StatusDisplay> = {
  [StudyWorkflowStatus.INCOMING]: {
    label: 'Descărcare...',
    color: 'default',
    bgColor: '#9e9e9e',
  },
  [StudyWorkflowStatus.NEW]: {
    label: 'Nou',
    color: 'info',
    bgColor: '#1976d2',
  },
  [StudyWorkflowStatus.ASSIGNED]: {
    label: 'Alocat',
    color: 'secondary',
    bgColor: '#9c27b0',
  },
  [StudyWorkflowStatus.IN_PROGRESS]: {
    label: 'În lucru',
    color: 'warning',
    bgColor: '#ed6c02',
  },
  [StudyWorkflowStatus.COMPLETED]: {
    label: 'Finalizat',
    color: 'success',
    bgColor: '#2e7d32',
  },
  [StudyWorkflowStatus.EXTERNAL]: {
    label: 'Extern',
    color: 'default',
    bgColor: '#757575',
  },
  [StudyWorkflowStatus.HISTORICAL]: {
    label: 'Istoric',
    color: 'default',
    bgColor: '#616161',
  },
};

/**
 * Get display properties for a study status
 */
export function getStatusDisplay(study: { workflow_status?: string; status?: string }): StatusDisplay {
  const effective = getEffectiveStatus(study);
  return statusDisplayMap[effective] || statusDisplayMap[StudyWorkflowStatus.NEW];
}

// Priority display helpers

export const priorityDisplayMap: Record<string, { label: string; color: string }> = {
  [StudyPriority.ROUTINE]: { label: 'Normal', color: '#4caf50' },
  [StudyPriority.URGENT]: { label: 'Urgent', color: '#ff9800' },
  [StudyPriority.STAT]: { label: 'STAT', color: '#f44336' },
};

export function getPriorityDisplay(priority?: string): { label: string; color: string } {
  if (!priority) return priorityDisplayMap[StudyPriority.ROUTINE];
  return priorityDisplayMap[priority] || priorityDisplayMap[StudyPriority.ROUTINE];
}
